import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save } from "lucide-react";
import { updatePipeline } from "@/lib/api";
import { toast } from "sonner";

const STAGES = ["Identified", "Researched", "Pitched", "Meeting Done", "Proposal", "Won", "Lost"];

export const PipelineTracker = ({ reportId, accountName, initial = {} }) => {
  const [data, setData] = useState({
    stage: initial.stage || "Identified",
    opportunity: initial.opportunity || "",
    deal_value: initial.deal_value || "",
    key_contact: initial.key_contact || "",
    last_interaction: initial.last_interaction || "",
    next_step: initial.next_step || "",
    probability: initial.probability || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const onSave = async () => {
    setSaving(true);
    try {
      await updatePipeline(reportId, data);
      toast.success("Pipeline updated");
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "h-10 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm font-mono-data placeholder:text-[#525252] focus-visible:ring-1 focus-visible:ring-[#0A84FF]";

  return (
    <div className="bg-[#141414] border border-[#262626] rounded-sm" data-testid="pipeline-tracker">
      <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
        <div>
          <div className="kpi-label">/ CRM-LITE · LIVE PIPELINE</div>
          <div className="text-sm text-white font-display tracking-tight mt-0.5">{accountName}</div>
        </div>
        <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="save-pipeline-btn"
          className="h-9 px-4 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2 transition-colors disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5"/>
          <span className="text-xs font-mono-data tracking-wider uppercase">{saving ? "Saving…" : "Save"}</span>
        </Button>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="kpi-label">Stage</label>
          <Select value={data.stage} onValueChange={(v) => setData({ ...data, stage: v })}>
            <SelectTrigger data-testid="pipeline-stage-select" className="h-10 bg-[#0A0A0A] border-[#262626] rounded-sm text-sm focus:ring-1 focus:ring-[#0A84FF]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626] rounded-sm">
              {STAGES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`stage-option-${s.toLowerCase().replace(/\s/g, '-')}`} className="text-sm font-mono-data">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="kpi-label">Probability</label>
          <Input data-testid="pipeline-probability" value={data.probability} onChange={set("probability")} placeholder="e.g. 30%" className={inputCls}/>
        </div>
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <label className="kpi-label">Opportunity</label>
          <Input data-testid="pipeline-opportunity" value={data.opportunity} onChange={set("opportunity")} placeholder="Headline opportunity" className={inputCls}/>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="kpi-label">Deal Value</label>
          <Input data-testid="pipeline-deal-value" value={data.deal_value} onChange={set("deal_value")} placeholder="$X-Y M" className={inputCls}/>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="kpi-label">Key Contact</label>
          <Input data-testid="pipeline-key-contact" value={data.key_contact} onChange={set("key_contact")} placeholder="Name / role" className={inputCls}/>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="kpi-label">Last Interaction</label>
          <Input data-testid="pipeline-last-interaction" value={data.last_interaction} onChange={set("last_interaction")} placeholder="e.g. Discovery call · Mar 12" className={inputCls}/>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="kpi-label">Next Step</label>
          <Input data-testid="pipeline-next-step" value={data.next_step} onChange={set("next_step")} placeholder="e.g. Send proposal" className={inputCls}/>
        </div>
      </div>
    </div>
  );
};

export default PipelineTracker;
